import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "sonner";
import { useApi } from "@/hooks/useApi";
import { type NamedOrg, orgApiPath } from "@/lib/named-org";
function pickFields<T extends object>(source: T, update: Partial<T>): Partial<T> {
  const picked: Partial<T> = {};
  for (const field of Object.keys(update) as (keyof T)[]) {
    picked[field] = source[field];
  }
  return picked;
}

/**
 * Query and mutation hooks for a member's per-org preferences that the
 * backend serves at `/user/github/orgs/{org}/{resource}`, every field
 * resolved to its default.
 *
 * Each control saves independently, so two mutations can overlap. Every
 * mutation callback only touches the fields its own update carried: a
 * delayed failure of one save must not roll back another save that already
 * landed.
 */
export function createUserOrgSettingsHooks<T extends object>({
  resource,
  errorMessage
}: {
  resource: string;
  errorMessage: string;
}) {
  function queryKey(org: NamedOrg | null | undefined) {
    return [resource, org] as const;
  }
  function useSettings(org, t0) {
    const $ = _c(10);
    let t1;
    if ($[0] !== t0) {
      t1 = t0 === undefined ? {} : t0;
      $[0] = t0;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    const {
      suspense: t2
    } = t1;
    const suspense = t2 === undefined ? false : t2;
    const api = useApi();
    let t3;
    if ($[2] !== org) {
      t3 = queryKey(org);
      $[2] = org;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    let t4;
    if ($[4] !== api || $[5] !== org) {
      t4 = async () => {
        const response = await api.get(`${orgApiPath(org)}/${resource}`);
        return response.data;
      };
      $[4] = api;
      $[5] = org;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    const t5 = !!org;
    let t6;
    if ($[7] !== suspense || $[8] !== t5) {
      t6 = {
        enabled: t5,
        suspense,
        useErrorBoundary: false
      };
      $[7] = suspense;
      $[8] = t5;
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    return useQuery(t3, t4, t6);
  }
  function useUpdateSettings(org) {
    const $ = _c(18);
    const api = useApi();
    const queryClient = useQueryClient();
    let t0;
    if ($[0] !== api || $[1] !== org) {
      t0 = async update => {
        const response = await api.put(`${orgApiPath(org)}/${resource}`, update);
        return response.data;
      };
      $[0] = api;
      $[1] = org;
      $[2] = t0;
    } else {
      t0 = $[2];
    }
    let t1;
    if ($[3] !== org) {
      t1 = queryKey(org);
      $[3] = org;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    let t2;
    if ($[5] !== org || $[6] !== queryClient) {
      t2 = async update_0 => {
        if (!org) {
          return;
        }
        const key = queryKey(org);
        await queryClient.cancelQueries(key);
        const current = queryClient.getQueryData(key);
        if (!current) {
          return {
            org,
            previous: undefined
          };
        }
        queryClient.setQueryData(key, {
          ...current,
          ...update_0
        });
        return {
          org,
          previous: pickFields(current, update_0)
        };
      };
      $[5] = org;
      $[6] = queryClient;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    let t3;
    let t4;
    let t5;
    if ($[8] !== queryClient) {
      t3 = (_error, _update, context) => {
        if (context?.previous) {
          const key_0 = queryKey(context.org);
          const current_0 = queryClient.getQueryData(key_0);
          if (current_0) {
            queryClient.setQueryData(key_0, {
              ...current_0,
              ...context.previous
            });
          }
        }
        toast.error(errorMessage);
      };
      t4 = (data, update_1, context_0) => {
        if (!context_0) {
          return;
        }
        const key_1 = queryKey(context_0.org);
        const current_1 = queryClient.getQueryData(key_1);
        queryClient.setQueryData(key_1, current_1 ? {
          ...current_1,
          ...pickFields(data, update_1)
        } : data);
      };
      t5 = (_data, _error_0, _update_0, context_1) => {
        if (!context_1) {
          return;
        }
        const key_2 = queryKey(context_1.org);
        if (queryClient.isMutating({
          mutationKey: key_2
        }) <= 1) {
          queryClient.invalidateQueries(key_2);
        }
      };
      $[8] = queryClient;
      $[9] = t3;
      $[10] = t4;
      $[11] = t5;
    } else {
      t3 = $[9];
      t4 = $[10];
      t5 = $[11];
    }
    let t6;
    if ($[12] !== t1 || $[13] !== t2 || $[14] !== t3 || $[15] !== t4 || $[16] !== t5) {
      t6 = {
        mutationKey: t1,
        onMutate: t2,
        onError: t3,
        onSuccess: t4,
        onSettled: t5
      };
      $[12] = t1;
      $[13] = t2;
      $[14] = t3;
      $[15] = t4;
      $[16] = t5;
      $[17] = t6;
    } else {
      t6 = $[17];
    }
    return useMutation(t0, t6);
  }
  return {
    queryKey,
    useSettings,
    useUpdateSettings
  };
}